import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Check, Clock } from "lucide-react";

import { ToolbarSticky } from "@/components/wyvio-ui/signature/ToolbarSticky";
import { HeroDossier } from "@/components/wyvio-ui/signature/HeroDossier";
import { CockpitTimeline } from "@/components/wyvio-ui/signature/CockpitTimeline";
import { NextActionPanel } from "@/components/wyvio-ui/signature/NextActionPanel";

/**
 * DossierMockup — Fiche dossier signature WYVIO.
 * Mission unique : "Quelle est la prochaine action qui fera avancer cette vente ?"
 *
 * Règle "50%" appliquée : ne restent que
 *   • Le véhicule (hero immersif)
 *   • La timeline cockpit 13 étapes
 *   • LA prochaine action + son contexte immédiat
 * Rien d'autre.
 *
 * Aucune image de démo : silhouette premium WYVIO — l'IA se chargera de
 * sélectionner automatiquement la meilleure photo côté production.
 */

const DOSSIER = {
  vehicle: "Porsche 911 GT3",
  variant: "Coupé · Boîte PDK · Craie",
  plate: "AA-042-XR",
  client: "Marc Ballaud",
  clientLocation: "Retrait boutique · Paris 8e",
  commercial: "Stéphane Vidal",
  orderNumber: "BC-2026-0412",
  silhouette: "coupe",
  image: null,
  statusChip: "encours",
  statusLabel: "En attente client",
};

// Étape 10 : "Créneaux proposés" — le client vient de choisir un créneau.
// Prochaine action agence : confirmer ce créneau pour verrouiller la livraison.
const CURRENT_STEP = 10;

export default function DossierMockup() {
  return (
    <>
      <Helmet>
        <title>{`Fiche dossier — ${DOSSIER.vehicle} · wyvio`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="wyvio-signature-bg font-neue-haas min-h-screen">
        <ToolbarSticky />

        <main className="relative z-10 mx-auto max-w-[1400px] px-12 pb-32 pt-16">
          {/* Fil d'Ariane ultra-discret + retour */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-10 flex items-center justify-between"
          >
            <Link
              to="/mockups/dashboard-typo-neue-haas"
              data-testid="back-to-dashboard"
              className="group inline-flex items-center gap-2 text-[12.5px] font-medium text-[#6B6B67] transition-colors hover:text-[#151515]"
            >
              <ArrowLeft
                className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5"
                strokeWidth={2}
              />
              Retour au centre de pilotage
            </Link>

            <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E8E2] bg-white/70 px-3 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.2em] text-[#6B6B67] backdrop-blur">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#151515]" />
              Mockup · Fiche dossier
            </div>
          </motion.div>

          {/* Hero véhicule immersif */}
          <section className="mb-8" data-testid="section-hero">
            <HeroDossier dossier={DOSSIER} testid="hero-dossier" />
          </section>

          {/* Bloc identité — 4 infos essentielles */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            data-testid="section-identity"
            className="mb-20 grid grid-cols-4 gap-8 border-b border-[#E8E8E2] pb-10"
          >
            {[
              { label: "Véhicule", value: DOSSIER.vehicle },
              { label: "Client", value: DOSSIER.client },
              { label: "Commercial", value: DOSSIER.commercial },
              { label: "Bon de commande", value: DOSSIER.orderNumber, mono: true },
            ].map((item, i) => (
              <div key={i}>
                <p className="text-[10.5px] font-medium uppercase tracking-[0.22em] text-[#8A8A80]">
                  {item.label}
                </p>
                <p
                  className={`mt-3 text-[16px] font-medium tracking-[-0.01em] text-[#151515] ${
                    item.mono ? "wyvio-tabular" : ""
                  }`}
                >
                  {item.value}
                </p>
              </div>
            ))}
          </motion.section>

          {/* Cockpit : Timeline 13 étapes + Next Action */}
          <section
            className="grid grid-cols-12 gap-10"
            data-testid="section-cockpit"
          >
            {/* Timeline verticale — colonne gauche */}
            <div className="col-span-5">
              <p className="mb-8 text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#8A8A80]">
                Étapes du dossier
              </p>
              <CockpitTimeline
                current={CURRENT_STEP}
                testid="cockpit-timeline"
              />
            </div>

            {/* Zone d'action unique — colonne droite */}
            <div className="col-span-7">
              <NextActionPanel
                overline="Prochaine action"
                title="Confirmez le créneau choisi par Marc."
                why="Marc a sélectionné le créneau du mardi 11 mars à 10h30 il y a 12 minutes."
                consequence="Une fois confirmé, le client recevra sa confirmation par SMS et email, le planning agence sera mis à jour et le dossier passera automatiquement à l'étape Livraison programmée."
                meta="Choix effectué il y a 12 minutes"
                cta="Confirmer mardi 11 mars · 10h30"
                helperText="La modification restera possible pendant 1 h après confirmation."
                testid="next-action"
              >
                <ContextSlot />
              </NextActionPanel>

              {/* Sous la Next Action : uniquement l'activité récente pertinente */}
              <RecentActivity className="mt-10" />
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

/**
 * ContextSlot — apparaît UNIQUEMENT parce que l'étape en cours est "Créneaux".
 * Si on était à l'étape "Solde reçu", ce bloc serait remplacé par le récap financier.
 * Le contexte suit toujours la Next Action, jamais l'inverse.
 */
function ContextSlot() {
  const SLOTS = [
    {
      id: 1,
      day: "Lundi 10 mars",
      time: "17h00",
      status: "declined",
      note: "Refusé par le client",
    },
    {
      id: 2,
      day: "Mardi 11 mars",
      time: "10h30",
      status: "chosen",
      note: "Choix du client",
    },
    {
      id: 3,
      day: "Mercredi 12 mars",
      time: "14h00",
      status: "pending",
      note: "Disponible",
    },
  ];

  return (
    <div>
      <p className="text-[10.5px] font-medium uppercase tracking-[0.22em] text-[#8A8A80]">
        Créneaux proposés
      </p>

      <div className="mt-6 space-y-3">
        {SLOTS.map((s, i) => {
          const isChosen = s.status === "chosen";
          const isDeclined = s.status === "declined";
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.08 * i + 0.3,
                duration: 0.4,
              }}
              className={`flex items-center gap-5 rounded-[16px] px-5 py-4 transition-all ${
                isChosen
                  ? "bg-[#151515] text-white"
                  : "bg-[#F2F2EE] text-[#151515]"
              }`}
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
                style={{
                  background: isChosen
                    ? "#C8FF2F"
                    : isDeclined
                      ? "rgba(21,21,21,0.05)"
                      : "rgba(21,21,21,0.06)",
                }}
              >
                {isChosen ? (
                  <Check className="h-4 w-4 text-[#151515]" strokeWidth={2.4} />
                ) : (
                  <Clock
                    className={`h-4 w-4 ${
                      isDeclined ? "text-[#8A8A80]" : "text-[#6B6B67]"
                    }`}
                    strokeWidth={1.8}
                  />
                )}
              </div>

              <div className="flex flex-1 items-baseline justify-between">
                <div>
                  <p
                    className={`text-[14px] font-medium tracking-[-0.005em] ${
                      isChosen ? "" : isDeclined ? "line-through opacity-60" : ""
                    }`}
                  >
                    {s.day} · <span className="wyvio-tabular">{s.time}</span>
                  </p>
                  <p
                    className={`mt-0.5 text-[11.5px] font-normal ${
                      isChosen ? "text-white/60" : "text-[#8A8A80]"
                    }`}
                  >
                    {s.note}
                  </p>
                </div>
                {isChosen && (
                  <span className="text-[9.5px] font-medium uppercase tracking-[0.22em] text-[#C8FF2F]">
                    Choix client
                  </span>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * RecentActivity — activité récente ultra-condensée.
 * Pas d'onglets messages/documents/paiements : uniquement le dernier signal utile
 * qui aide à comprendre l'état du dossier en une glance.
 */
function RecentActivity({ className = "" }) {
  const EVENTS = [
    {
      time: "il y a 12 min",
      text: "Marc a choisi le créneau de mardi 11 mars 10h30.",
      strong: true,
    },
    {
      time: "il y a 2 h",
      text: "3 créneaux ont été proposés à Marc par email et SMS.",
    },
    {
      time: "hier",
      text: "Solde de 12 500 € reçu par virement SEPA.",
    },
    {
      time: "il y a 3 j",
      text: "Contrôle qualité validé par Kevin (14 photos ajoutées).",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35, duration: 0.5 }}
      className={`shadow-wyvio-elev rounded-[24px] bg-white p-10 ${className}`}
      data-testid="recent-activity"
    >
      <p className="text-[10.5px] font-medium uppercase tracking-[0.28em] text-[#8A8A80]">
        Activité récente
      </p>

      <ul className="mt-8 space-y-6">
        {EVENTS.map((e, i) => (
          <li key={i} className="flex items-start gap-5">
            <span
              aria-hidden
              className="mt-1.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full"
              style={{ background: e.strong ? "#151515" : "#C4C4B8" }}
            />
            <div className="flex-1">
              <p
                className={`${
                  e.strong ? "text-[#151515]" : "text-[#4A4A46]"
                } text-[14px] font-normal leading-[1.5] tracking-[-0.005em]`}
              >
                {e.text}
              </p>
              <p className="mt-1 text-[11.5px] font-normal text-[#8A8A80]">
                {e.time}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}
